import { dayJs } from './dayjs'
import { useGlobalI18n } from './global-i18n'

export const hasTimeComponent = (startDateInput: string, endDateInput: string) => {
  const dayjs = dayJs()

  const startDate = dayjs(startDateInput).tz('Europe/Amsterdam')
  const endDate = dayjs(endDateInput).tz('Europe/Amsterdam')

  const noTime = (input: string, d: ReturnType<typeof dayjs>) => {
    const match = input.match(/T(\d{2}):(\d{2})/)
    if (!match) return true
    if (match[1] === '00' && match[2] === '00') return true
    return d.hour() === 0 && d.minute() === 0
  }

  return !noTime(startDateInput, startDate) || !noTime(endDateInput, endDate)
}

// An en dash sits tight between simple parts ("28–30 mei", "2024–2026") but
// takes spaces once both sides are compound, so it reads as the break between
// two dates instead of binding to whatever sits next to it — without this,
// "20.00–14 juni" looks like a range from a time to a date. The space before
// the dash is non-breaking so the dash can never start a line on its own.
export const joinRange = (start: string, end: string) =>
  start.includes(' ') && end.includes(' ') ? `${start}\u00a0– ${end}` : `${start}–${end}`

export const getFormattedDateRange = (startDateInput: string, endDateInput?: string | null, options?: { forceYear?: boolean, showFullYearIfFullYear?: boolean, showMonthIfFullMonth?: boolean, hideTime?: boolean, isOngoing?: boolean }) => {
  if (!startDateInput || (!endDateInput && !options?.isOngoing)) {
    return ''
  }

  const dayjs = dayJs()
  const { locale, t } = useGlobalI18n()

  // An ongoing role has no end date at all -- it runs up to the present, so the
  // range closes on the word rather than on whatever year was last entered
  // ("2018–heden", not "2018–2026").
  if (options?.isOngoing) {
    const start = dayjs(startDateInput).tz('Europe/Amsterdam')
    const isYearStart = start.month() === 0 && start.date() === 1
    const startLabel = options?.showFullYearIfFullYear && isYearStart
      ? start.format('YYYY')
      : start.format(locale.value === 'en' ? 'D MMMM, YYYY' : 'D MMMM YYYY')
    return joinRange(startLabel, t('labels.ongoing'))
  }

  // narrows the rest of the function back to a closed range
  if (!endDateInput) {
    return ''
  }

  const startDate = dayjs(startDateInput).tz('Europe/Amsterdam')
  const endDate = dayjs(endDateInput).tz('Europe/Amsterdam')

  const isSameYear = startDate.isSame(endDate, 'year')
  const isSameMonth = startDate.isSame(endDate, 'month')
  const isSameDay = startDate.isSame(endDate, 'day')
  const isCurrentYear = false //isSameYear && startDate.isSame(dayjs(), 'year')

  // a period (a tutorship, a year as participant) is stored with whatever
  // time of day happened to be entered; the time says nothing, so it is left out
  const hasTime = !options?.hideTime && hasTimeComponent(startDateInput, endDateInput)

  // Neither language writes a leading zero in a spelled-out date (5 mei, not
  // 05 mei). They differ only on the time separator: Dutch takes a point,
  // English a colon.
  const dayFmt = 'D'
  const timeFmt = locale.value === 'en' ? 'HH:mm' : 'HH.mm'
  // House style: English sets off the year with a comma ("28 May – 14 June,
  // 2026"). It is the American convention on a day-first date rather than a
  // British one, and Dutch allows it in no form, so nl keeps the plain space.
  // The one exception is a year that is followed by a time: "28 May 2026,
  // 20:00" needs its single comma to separate date from time, and a second one
  // ("28 May, 2026, 20:00") would leave the year floating between two commas.
  const yearSuffix = locale.value === 'en' ? ', YYYY' : ' YYYY'
  const yearSuffixBeforeTime = ' YYYY'
  const noYear = isCurrentYear && !options?.forceYear
  const yearFmt = noYear ? '' : yearSuffix
  const yearFmtBeforeTime = noYear ? '' : yearSuffixBeforeTime

  const isFullYearRange = startDate.month() === 0 && startDate.date() === 1
    && endDate.month() === 11 && endDate.date() === 31

  // first of a month through the last of a month: the days add nothing, so the
  // range reads on months alone ("September 2024 – August 2026")
  const isFullMonthRange = startDate.date() === 1 && endDate.date() === endDate.daysInMonth()

  if (options?.showFullYearIfFullYear && isFullYearRange) {
    return isSameYear
      ? startDate.format('YYYY')
      : joinRange(startDate.format('YYYY'), endDate.format('YYYY'))
  }
  else if (options?.showMonthIfFullMonth && isFullMonthRange) {
    if (isSameYear && isSameMonth) return startDate.format('MMMM YYYY')
    if (isSameYear) return joinRange(startDate.format('MMMM'), endDate.format('MMMM YYYY'))
    return joinRange(startDate.format('MMMM YYYY'), endDate.format('MMMM YYYY'))
  }
  else if (isSameYear && isSameMonth && isSameDay) {
    if (hasTime) {
      return joinRange(startDate.format(`${dayFmt} MMMM${yearFmtBeforeTime}, ${timeFmt}`), endDate.format(timeFmt))
    } else { 
      return startDate.format(`${dayFmt} MMMM${yearFmt}`)
    }
  } else if (isSameYear && isSameMonth) {
    if (hasTime) {
      return joinRange(startDate.format(`${dayFmt} MMMM, ${timeFmt}`), endDate.format(`${dayFmt} MMMM${yearFmtBeforeTime}, ${timeFmt}`))
    } else {
      return joinRange(startDate.format(dayFmt), endDate.format(`${dayFmt} MMMM${yearFmt}`))
    }
  } else if (isSameYear) {
    if (hasTime) {
      return joinRange(startDate.format(`${dayFmt} MMMM, ${timeFmt}`), endDate.format(`${dayFmt} MMMM${yearFmtBeforeTime}, ${timeFmt}`))
    } else {
      return joinRange(startDate.format(`${dayFmt} MMMM`), endDate.format(`${dayFmt} MMMM${yearFmt}`))
    }
  } else {
    if (hasTime) {
      return joinRange(startDate.format(`${dayFmt} MMMM${yearSuffixBeforeTime}, ${timeFmt}`), endDate.format(`${dayFmt} MMMM${yearSuffixBeforeTime}, ${timeFmt}`))
    } else {
      return joinRange(startDate.format(`${dayFmt} MMMM${yearSuffix}`), endDate.format(`${dayFmt} MMMM${yearSuffix}`))
    }
  }
}

// A person's period is labelled on years alone, wherever it shows: the year it
// falls in ("2024") or the years it spans ("2024–2026"), whatever the exact
// days it was entered with. An ongoing role has no end, so it closes on
// "heden"/"current" instead of a year ("2018–heden").
export const getFormattedYearRange = (startDateInput: string, endDateInput?: string | null, options?: { isOngoing?: boolean }) => {
  if (!startDateInput) return ''
  const dayjs = dayJs()
  const startYear = dayjs(startDateInput).tz('Europe/Amsterdam').format('YYYY')
  if (options?.isOngoing) {
    const { t } = useGlobalI18n()
    return joinRange(startYear, t('labels.ongoing'))
  }
  const endYear = endDateInput ? dayjs(endDateInput).tz('Europe/Amsterdam').format('YYYY') : startYear
  return startYear === endYear ? startYear : joinRange(startYear, endYear)
}
